import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { pushToast } from '../lib/toast';
import { TaskRow } from './TaskRow';
import { TaskEditor } from './TaskEditor';
import { empty, labels, pick } from '@shared/copy';

const TITLES: Record<string, { title: string; tagline: string; emptyKey: keyof typeof empty }> = {
  today: { title: 'Today', tagline: labels.todayTagline, emptyKey: 'today' },
  inbox: { title: 'Inbox', tagline: labels.inboxTagline, emptyKey: 'inbox' },
  next: { title: 'Next', tagline: labels.nextTagline, emptyKey: 'next' },
  waiting: { title: 'Waiting', tagline: labels.waitingTagline, emptyKey: 'waiting' },
  scheduled: { title: 'Scheduled', tagline: labels.scheduledTagline, emptyKey: 'scheduled' },
  someday: { title: 'Someday', tagline: labels.somedayTagline, emptyKey: 'someday' },
  logbook: { title: 'Logbook', tagline: labels.logbookTagline, emptyKey: 'logbook' },
};

export function TaskListView({ viewName }: { viewName: string }) {
  const tz = new Date().getTimezoneOffset();
  const q = useQuery({
    queryKey: ['view', viewName, tz],
    queryFn: () => api.view(viewName, { tz: String(tz) }),
    refetchOnWindowFocus: true,
  });
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const meta = TITLES[viewName] ?? TITLES.today;
  const tasks = q.data ?? [];

  const grouped = useMemo(() => {
    if (viewName !== 'today') return null;
    const focus = tasks.filter((t) => t.focus === 1);
    const overdue = tasks.filter((t) => t.focus !== 1 && t.due_at && t.due_at < Date.now());
    const scheduledToday = tasks.filter((t) => t.focus !== 1 && (!t.due_at || t.due_at >= Date.now()) && t.defer_at);
    return { focus, overdue, scheduledToday };
  }, [tasks, viewName]);

  // Flat ordered list of all rendered task ids — used by keyboard navigation.
  const orderedIds = useMemo(() => {
    if (grouped) return [...grouped.focus, ...grouped.overdue, ...grouped.scheduledToday].map((t: any) => t.id);
    return tasks.map((t: any) => t.id);
  }, [grouped, tasks]);

  // Default selection to first row when data lands.
  useEffect(() => {
    if (selectedId && orderedIds.includes(selectedId)) return;
    setSelectedId(orderedIds[0] ?? null);
  }, [orderedIds, selectedId]);

  // Keyboard navigation. Active when no modal/input is focused and editor is closed.
  useEffect(() => {
    if (editingId) return;
    function isTextEditable(el: Element | null): boolean {
      if (!el) return false;
      const tag = (el as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if ((el as HTMLElement).isContentEditable) return true;
      return false;
    }

    function move(by: number) {
      if (orderedIds.length === 0) return;
      const idx = selectedId ? orderedIds.indexOf(selectedId) : -1;
      const next = idx < 0 ? 0 : Math.max(0, Math.min(orderedIds.length - 1, idx + by));
      const id = orderedIds[next];
      setSelectedId(id);
      // Bring into view
      requestAnimationFrame(() => {
        const node = document.querySelector(`[data-task-id="${id}"]`) as HTMLElement | null;
        node?.scrollIntoView({ block: 'nearest' });
      });
    }

    async function patchSelected(patch: any, optimistic?: (t: any) => any) {
      if (!selectedId) return;
      if (optimistic) {
        qc.setQueriesData({ queryKey: ['view'] }, (list: any) =>
          Array.isArray(list) ? list.map((t: any) => (t.id === selectedId ? optimistic(t) : t)) : list,
        );
      }
      try {
        await api.updateTask(selectedId, patch);
        qc.invalidateQueries({ queryKey: ['view'] });
        qc.invalidateQueries({ queryKey: ['stats'] });
      } catch (e: any) {
        pushToast(e?.message ?? 'Update failed.', 'error');
        qc.invalidateQueries({ queryKey: ['view'] });
      }
    }

    async function toggleSelected() {
      if (!selectedId) return;
      const t = tasks.find((x: any) => x.id === selectedId);
      if (!t) return;
      const next = !(t.status === 'done');
      qc.setQueriesData({ queryKey: ['view'] }, (list: any) =>
        Array.isArray(list)
          ? list.map((x: any) => (x.id === selectedId ? { ...x, status: next ? 'done' : 'next', completed_at: next ? Date.now() : null } : x))
          : list,
      );
      try {
        await (next ? api.completeTask(selectedId) : api.uncompleteTask(selectedId));
        qc.invalidateQueries({ queryKey: ['view'] });
        qc.invalidateQueries({ queryKey: ['stats'] });
      } catch {
        qc.invalidateQueries({ queryKey: ['view'] });
      }
    }

    async function deleteSelected() {
      if (!selectedId) return;
      if (!confirm('Delete this task?')) return;
      try {
        await api.deleteTask(selectedId);
        const idx = orderedIds.indexOf(selectedId);
        const nextId = orderedIds[idx + 1] ?? orderedIds[idx - 1] ?? null;
        setSelectedId(nextId);
        qc.invalidateQueries({ queryKey: ['view'] });
        qc.invalidateQueries({ queryKey: ['stats'] });
      } catch (e: any) {
        pushToast(e?.message ?? 'Delete failed.', 'error');
      }
    }

    const STATUS_KEY: Record<string, string> = {
      '1': 'inbox',
      '2': 'next',
      '3': 'waiting',
      '4': 'scheduled',
      '5': 'someday',
      '6': 'done',
    };

    const onKey = (e: KeyboardEvent) => {
      if (isTextEditable(document.activeElement)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key) {
        case 'j':
        case 'ArrowDown':
          e.preventDefault();
          move(1);
          return;
        case 'k':
        case 'ArrowUp':
          e.preventDefault();
          move(-1);
          return;
        case 'Enter':
        case 'o':
        case 'e':
          if (selectedId) {
            e.preventDefault();
            setEditingId(selectedId);
          }
          return;
        case ' ': // Space
        case 'x':
          if (selectedId) {
            e.preventDefault();
            toggleSelected();
          }
          return;
        case 's':
          if (selectedId) {
            e.preventDefault();
            const t = tasks.find((x: any) => x.id === selectedId);
            const wasFocus = !!t?.focus;
            patchSelected({ focus: !wasFocus }, (row) => ({ ...row, focus: wasFocus ? 0 : 1 }));
          }
          return;
        case 'Delete':
        case 'Backspace':
          if (selectedId) {
            e.preventDefault();
            deleteSelected();
          }
          return;
        default:
          if (selectedId && STATUS_KEY[e.key]) {
            e.preventDefault();
            const newStatus = STATUS_KEY[e.key];
            patchSelected({ status: newStatus }, (row) => ({ ...row, status: newStatus }));
          }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingId, orderedIds, qc, selectedId, tasks]);

  return (
    <div className="mx-auto max-w-3xl px-3 md:px-6 py-4 md:py-8">
      <header className="mb-3 md:mb-5">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">{meta.title}</h1>
        <p className="text-sm text-zinc-500 mt-0.5">{meta.tagline}</p>
      </header>

      {q.isLoading && <div className="text-sm text-zinc-500 px-1 py-6">Fetching…</div>}

      {!q.isLoading && tasks.length === 0 && (
        <div className="text-center text-zinc-500 py-12">{pick(empty[meta.emptyKey], Math.floor(Date.now() / 60_000))}</div>
      )}

      {grouped ? (
        <>
          <Group title="Focus" emptyHint="Nothing flagged. Pick three." items={grouped.focus} selectedId={selectedId} onSelect={setSelectedId} onOpen={setEditingId} />
          <Group title="Overdue" emptyHint="" items={grouped.overdue} selectedId={selectedId} onSelect={setSelectedId} onOpen={setEditingId} />
          <Group title="Scheduled" emptyHint="" items={grouped.scheduledToday} selectedId={selectedId} onSelect={setSelectedId} onOpen={setEditingId} />
        </>
      ) : (
        <ul className="divide-y divide-zinc-800/70 rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
          {tasks.map((t) => (
            <TaskRow
              key={t.id}
              task={t as any}
              isSelected={selectedId === t.id}
              onSelect={() => setSelectedId(t.id)}
              onOpen={() => setEditingId(t.id)}
            />
          ))}
        </ul>
      )}

      {editingId && (
        <TaskEditor taskId={editingId} onClose={() => setEditingId(null)} />
      )}
    </div>
  );
}

function Group({
  title,
  items,
  emptyHint,
  selectedId,
  onSelect,
  onOpen,
}: {
  title: string;
  items: any[];
  emptyHint: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
}) {
  if (items.length === 0 && !emptyHint) return null;
  return (
    <section className="mb-5">
      <h2 className="text-xs uppercase tracking-wider text-zinc-500 mb-2 px-1">{title}</h2>
      {items.length === 0 ? (
        <div className="text-sm text-zinc-600 px-1">{emptyHint}</div>
      ) : (
        <ul className="divide-y divide-zinc-800/70 rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
          {items.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              isSelected={selectedId === t.id}
              onSelect={() => onSelect(t.id)}
              onOpen={() => onOpen(t.id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
